import { useState } from "react";
import type { FormEvent } from "react";
import Breadcrumb from "@/components/base/Breadcrumb";

const FORM_SUBMIT_ADDR = "https://readdy.ai/api/form/db8bsuhrh5tg6socjsn0";

const values = [
  { icon: "ri-moon-clear-line", title: "午夜专属", desc: "围绕深夜时段与午夜氛围，专注打造沉浸式的夜间观影入口。" },
  { icon: "ri-price-tag-3-line", title: "免费高清", desc: "内容浏览与界面体验对用户免费开放，聚焦高清影视的清晰呈现。" },
  { icon: "ri-compass-3-line", title: "便捷发现", desc: "分类、筛选、搜索三位一体，帮助你用最少步骤找到想看的作品。" },
  { icon: "ri-shield-star-line", title: "内容一致", desc: "列表页与详情页信息严格对应，避免误导性的内容展示。" },
];

const serviceItems = [
  { icon: "ri-film-line", label: "电影内容", desc: "聚焦午夜长片与类型电影" },
  { icon: "ri-live-line", label: "剧集综艺", desc: "电视剧与综艺持续更新" },
  { icon: "ri-search-2-line", label: "搜索发现", desc: "关键词模糊匹配快速定位" },
  { icon: "ri-heart-3-line", label: "本地收藏", desc: "当前浏览器内保存想看清单" },
];

export default function AboutPage() {
  const [status, setStatus] = useState<"idle" | "submitting" | "success" | "error">("idle");
  const [message, setMessage] = useState("");
  const [formError, setFormError] = useState("");

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = event.currentTarget;
    const formData = new FormData(form);

    const honeypot = String(formData.get("website_alt") ?? "").trim();
    if (honeypot) {
      setStatus("success");
      setMessage("感谢你的留言，我们已经收到啦，会尽快回复你。");
      setFormError("");
      return;
    }

    setStatus("submitting");
    setFormError("");
    setMessage("");

    const payload = new URLSearchParams();
    formData.forEach((value, key) => {
      if (key === "website_alt") return;
      const text = String(value).trim();
      if (text.length === 0) return;
      payload.append(key, text);
    });

    try {
      const response = await fetch(FORM_SUBMIT_ADDR, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: payload.toString(),
      });
      const responseText = await response.text();
      let parsed: { code?: string; message?: string; meta?: { message?: string; detail?: string } } | null = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }
      const serverMsg = parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText;

      const isSpam = typeof serverMsg === "string" && serverMsg.toLowerCase().includes("spam");
      if (response.ok && parsed?.code === "OK" && !isSpam) {
        setStatus("success");
        setMessage("留言提交成功，感谢你联系我们，我们会尽快回复。");
        form.reset();
      } else {
        setStatus("error");
        setFormError(serverMsg && serverMsg.length < 200 ? serverMsg : "提交失败，请稍后重试。");
      }
    } catch {
      setStatus("error");
      setFormError("网络异常，提交失败，请稍后重试。");
    }
  };

  return (
    <div className="mx-auto max-w-page px-4 md:px-8 pt-8 md:pt-12">
      <Breadcrumb items={[{ label: "关于我们" }]} />

      <section className="mt-6 grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
        <div className="lg:col-span-7">
          <span className="inline-flex items-center gap-2 text-xs font-label tracking-[0.22em] text-primary-400 uppercase">
            <span className="w-6 h-px bg-primary-500/70"></span>
            About Us
          </span>
          <h1 className="mt-3 font-heading font-extrabold text-3xl md:text-5xl text-foreground-950 leading-tight">
            关于 午夜免费视频
          </h1>
          <p className="mt-5 text-sm md:text-base leading-relaxed text-foreground-700">
            午夜免费视频是一个面向午夜影视爱好者的免费高清视频门户。我们围绕深夜时段的观影习惯，
            聚合电影、电视剧、综艺等多种类型的午夜影视内容，为喜欢在夜里安静看片的用户，提供一个清晰、便捷、沉浸的内容发现空间。
          </p>
          <p className="mt-4 text-sm md:text-base leading-relaxed text-foreground-700">
            我们相信，好内容值得被更容易地找到。因此网站提供分类浏览、多条件筛选、关键词搜索与本地收藏，
            让每一次午夜打开，都能更快遇见想看的那一部作品。
          </p>
        </div>

        <div className="lg:col-span-5 rounded-lg bg-background-100 border border-background-300/60 p-6 md:p-7">
          <h2 className="font-heading font-bold text-lg text-foreground-950">服务宗旨</h2>
          <p className="mt-3 text-sm leading-relaxed text-foreground-600">
            以用户体验为中心，坚持免费开放、高清呈现与信息透明的原则，做午夜时分可靠而安静的影视陪伴者。
          </p>
          <div className="mt-5 grid grid-cols-2 gap-3">
            {serviceItems.map((item) => (
              <div key={item.label} className="rounded-lg bg-background-50 border border-background-300/60 p-3.5">
                <i className={`${item.icon} text-primary-400 text-xl`}></i>
                <p className="mt-2 text-sm font-medium text-foreground-900">{item.label}</p>
                <p className="mt-1 text-xs text-foreground-600">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="mt-12 md:mt-16">
        <h2 className="font-heading font-bold text-xl md:text-2xl text-foreground-950">我们的价值主张</h2>
        <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-5">
          {values.map((item) => (
            <div key={item.title} className="rounded-lg bg-background-100 border border-background-300/60 p-5">
              <span className="w-11 h-11 rounded-md bg-primary-500/15 text-primary-300 flex items-center justify-center">
                <i className={`${item.icon} text-xl`}></i>
              </span>
              <h3 className="mt-4 font-heading font-bold text-base text-foreground-950">{item.title}</h3>
              <p className="mt-2 text-sm text-foreground-600 leading-relaxed">{item.desc}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="mt-12 md:mt-16 grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
        <div className="lg:col-span-5">
          <h2 className="font-heading font-bold text-xl md:text-2xl text-foreground-950">联系我们</h2>
          <p className="mt-3 text-sm leading-relaxed text-foreground-600">
            对内容、体验或合作有任何想法，欢迎通过下面的表单给我们留言。我们会认真阅读每一条反馈，并尽快回复。
          </p>
          <div className="mt-6 space-y-4">
            <div className="flex items-center gap-3">
              <span className="w-10 h-10 rounded-md bg-accent-500/20 text-accent-300 flex items-center justify-center">
                <i className="ri-mail-line text-xl"></i>
              </span>
              <div>
                <p className="text-xs text-foreground-500">邮箱</p>
                <a href="mailto:hello@midnight-video.example.com" className="text-sm text-foreground-900 hover:text-primary-400 transition-colors">
                  hello@midnight-video.example.com
                </a>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <span className="w-10 h-10 rounded-md bg-accent-500/20 text-accent-300 flex items-center justify-center">
                <i className="ri-map-pin-line text-xl"></i>
              </span>
              <div>
                <p className="text-xs text-foreground-500">内容中心</p>
                <p className="text-sm text-foreground-900">线上运营 · 全天候开放</p>
              </div>
            </div>
          </div>

          <div className="mt-6 rounded-lg bg-background-100 border border-background-300/60 p-5">
            <h3 className="font-heading font-bold text-base text-foreground-950 flex items-center gap-2">
              <i className="ri-copyright-line text-primary-400"></i>
              版权声明
            </h3>
            <p className="mt-2 text-xs leading-relaxed text-foreground-600">
              本站所有影视名称、封面与简介均为内容演示用途，播放区域仅为界面展示，不提供真实视频流。
              网站内容不用于商业传播，如涉及相关权益，请通过上方邮箱与我们联系。
            </p>
          </div>
        </div>

        <div className="lg:col-span-7">
          <form
            id="midnight-contact-form"
            data-readdy-form
            onSubmit={handleSubmit}
            className="rounded-lg bg-background-100 border border-background-300/60 p-5 md:p-7"
          >
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <label className="flex flex-col gap-2">
                <span className="text-sm text-foreground-700">你的称呼</span>
                <input
                  type="text"
                  name="name"
                  required
                  placeholder="请输入你的称呼"
                  className="h-11 px-3.5 rounded-md bg-background-50 border border-background-300/70 text-sm text-foreground-950 placeholder:text-foreground-500 outline-none focus:border-primary-500/70 transition-colors"
                />
              </label>
              <label className="flex flex-col gap-2">
                <span className="text-sm text-foreground-700">邮箱地址</span>
                <input
                  type="email"
                  name="email"
                  required
                  placeholder="you@example.com"
                  className="h-11 px-3.5 rounded-md bg-background-50 border border-background-300/70 text-sm text-foreground-950 placeholder:text-foreground-500 outline-none focus:border-primary-500/70 transition-colors"
                />
              </label>
            </div>

            <label className="mt-4 flex flex-col gap-2">
              <span className="text-sm text-foreground-700">留言类型</span>
              <select
                name="subject"
                defaultValue="内容建议"
                className="h-11 px-3.5 rounded-md bg-background-50 border border-background-300/70 text-sm text-foreground-950 outline-none focus:border-primary-500/70 transition-colors cursor-pointer"
              >
                <option value="内容建议">内容建议</option>
                <option value="功能反馈">功能反馈</option>
                <option value="合作咨询">合作咨询</option>
                <option value="其他问题">其他问题</option>
              </select>
            </label>

            <label className="mt-4 flex flex-col gap-2">
              <span className="text-sm text-foreground-700">留言内容</span>
              <textarea
                name="message"
                required
                maxLength={500}
                rows={5}
                placeholder="说说你的想法，最多 500 字～"
                className="px-3.5 py-3 rounded-md bg-background-50 border border-background-300/70 text-sm text-foreground-950 placeholder:text-foreground-500 outline-none focus:border-primary-500/70 transition-colors resize-none"
              ></textarea>
            </label>

            <input
              type="text"
              name="website_alt"
              className="midnight-form-guard"
              tabIndex={-1}
              autoComplete="off"
              aria-hidden="true"
              readOnly
            />

            <div className="mt-5 flex flex-col sm:flex-row sm:items-center gap-3">
              <button
                type="submit"
                disabled={status === "submitting"}
                className="inline-flex items-center justify-center gap-2 h-12 px-6 rounded-md bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer disabled:opacity-60 disabled:cursor-not-allowed"
              >
                {status === "submitting" ? (
                  <>
                    <i className="ri-loader-4-line animate-spin"></i>
                    提交中...
                  </>
                ) : (
                  <>
                    <i className="ri-send-plane-line text-lg"></i>
                    提交留言
                  </>
                )}
              </button>
              <p className="text-xs text-foreground-500">提交后我们会在工作时间内尽快回复。</p>
            </div>

            {status === "success" && message && (
              <div className="mt-4 flex items-start gap-2.5 rounded-md bg-primary-500/12 border border-primary-500/40 px-4 py-3">
                <i className="ri-checkbox-circle-line text-primary-400 text-lg mt-0.5"></i>
                <p className="text-sm text-foreground-900">{message}</p>
              </div>
            )}
            {status === "error" && formError && (
              <div className="mt-4 flex items-start gap-2.5 rounded-md bg-accent-900/60 border border-accent-700/60 px-4 py-3">
                <i className="ri-error-warning-line text-accent-300 text-lg mt-0.5"></i>
                <p className="text-sm text-foreground-900">{formError}</p>
              </div>
            )}
          </form>
        </div>
      </section>
    </div>
  );
}